import { dummyPdfBytes } from "@/assets/dummy_bytes"
import { api } from "@/lib/api"
import {
	PaymentStatus,
	type COUPON_DISCOUNT,
	type PAYMENT_PLAN,
} from "@/types/form-types"

export function getPeriodType(frequency: string) {
	if (frequency === "PER_YEAR") {
		return "YEAR"
	} else if (frequency === "PER_SEMESTER") {
		return "SEMESTER"
	} else {
		return frequency
	}
}

export function getPlanType(frequency: string) {
	if (frequency === "PER_YEAR") {
		return "YEAR"
	} else if (frequency === "PER_SEMESTER") {
		return "SEMESTER"
	} else if (frequency === "ONE_TIME") {
		return "FULL"
	} else {
		return frequency
	}
}

export function getPlanName(frequency: string) {
	if (frequency === "PER_YEAR") {
		return "YEARLY"
	} else if (frequency === "PER_SEMESTER") {
		return "SEMESTER"
	} else if (frequency === "ONE_TIME") {
		return "ONE_TIME"
	} else {
		return frequency
	}
}

export function checkPaymentPlan(
	paymentPlan: PAYMENT_PLAN,
	couponPlan: COUPON_DISCOUNT,
	paymentType: "self" | "loan",
	lender_id?: string,
	tenure_in_months?: number,
) {
	if (paymentType === "self" && couponPlan.payment_mode === "SELF") {
		if (paymentPlan === "PER_SEMESTER" && couponPlan.plan === "SEMESTER")
			return true
		if (paymentPlan === "PER_YEAR" && couponPlan.plan === "YEARLY")
			return true
		if (paymentPlan === "ONE_TIME" && couponPlan.plan === "ONE_TIME")
			return true
	} else if (paymentType === "loan" && couponPlan.payment_mode === "LOAN") {
		if (
			lender_id !== couponPlan.lender_id ||
			tenure_in_months !== couponPlan.tenure_in_months
		) {
			return false
		}
		if (paymentPlan === "PER_SEMESTER" && couponPlan.plan === "SEMESTER")
			return true
		if (paymentPlan === "PER_YEAR" && couponPlan.plan === "YEARLY")
			return true
		if (paymentPlan === "ONE_TIME" && couponPlan.plan === "ONE_TIME")
			return true
	}
	return false
}

export const disabledStatuses = [
	PaymentStatus.PAYMENT_IN_PROGRESS,
	PaymentStatus.PAYMENT_SUCCESS,
	PaymentStatus.UNKNOWN,
	PaymentStatus.SERVICE_UNAVAILABLE,
	PaymentStatus.ORDER_CREATED_PENDING_LOAN_APPROVAL,
	PaymentStatus.LOAN_APPROVED_PENDING_LOAN_DISBURSEMENT,
	PaymentStatus.LOAN_DISBURSEMENT_RECEIVED,
]

export async function fileFromUrl(url: string, filename?: string) {
	try {
		const response = await api.get("media", {
			params: {
				path: url,
			},
			responseType: "arraybuffer",
		})
		const mimeType = response.headers["content-type"] || "application/pdf"

		let finalFilename = filename

		if (!finalFilename) {
			const contentDisposition = response.headers["content-disposition"]
			if (contentDisposition) {
				const filenameMatch = contentDisposition.match(
					/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
				)
				if (filenameMatch && filenameMatch[1]) {
					finalFilename = filenameMatch[1].replace(/['"]/g, "")
				}
			}
		}

		if (!finalFilename) {
			const urlFilename = url.split("/").pop()
			if (urlFilename && urlFilename.includes(".")) {
				finalFilename = urlFilename
			}
		}

		if (!finalFilename) {
			finalFilename = "document.pdf"
		}

		const blob = new Blob([response.data], { type: mimeType })
		return new File([blob], finalFilename, { type: mimeType })
	} catch (error) {
		console.error("Error creating File from URL:", error)
		throw error
	}
}

export const downloadFile = async (
	fetchURL: string,
	filename: string,
	setIsLoading: (isLoading: boolean) => void,
	alertMessage: string,
) => {
	try {
		setIsLoading(true)
		const response = await api.get(fetchURL, {
			responseType: "blob",
		})

		if (response.status !== 200) {
			throw new Error(alertMessage)
		}

		let fileData = response.data
		if (!fileData || fileData.size === 0) {
			// fallback dummy PDF data
			const dummyPdfBytesData = dummyPdfBytes

			fileData = new Blob([dummyPdfBytesData], {
				type: "application/pdf",
			})
		}

		const blob =
			fileData instanceof Blob
				? fileData
				: new Blob([fileData], { type: "application/pdf" })
		const url = window.URL.createObjectURL(blob)

		const a = document.createElement("a")
		a.href = url
		a.download = filename
		document.body.appendChild(a)
		a.click()
		a.remove()
		window.URL.revokeObjectURL(url)
	} catch {
		alert(alertMessage)
	} finally {
		setIsLoading(false)
	}
}
